import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';

import '../../styles/global.css';
import '../../styles/buttons.css';
import '../../styles/popup.css';

import useAuthenticate from "../authentication/authenticate.js";
import imageUpload from '../general/imageUpload.js';

import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Tooltip from '@mui/material/Tooltip';

import { message, Upload } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const EditPropertyForm = ({ property, propertyId, onClose, onPropertyUpdated }) => {
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        price: '',
        lotArea: '',
        floorArea: '',
        developer: '',
        description: '',
        amenities: [],
    });
    const [amenityInput, setAmenityInput] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [propertyImages, setPropertyImages] = useState([]);

    const { user } = useAuthenticate();
    const activeAccountId = Object.values(user?.user?.active || {})[0];

    useEffect(() => {
        console.log("Editing property:", property);
    }, [property]);

    useEffect(() => {
        if (property) {
            setFormData({
                name: property.name || '',
                type: property.type || '',
                price: property.price || '',
                lotArea: property.lotArea || '',
                floorArea: property.floorArea || '',
                developer: property.developer || '',
                description: property.description || '',
                amenities: property.amenities || [],
            });
        }

        if (property.images && property.images.length > 0) {
            const img = property.images[0];
            setPropertyImages([{
                uid: img._id,
                name: img.altText || "property image",
                status: "done",
                url: img.key,
            }]);
        }
    }, [property]);

    const handleAddAmenity = () => {
        if (amenityInput.trim()) {
            setFormData((prevData) => ({
                ...prevData,
                amenities: [...prevData.amenities, amenityInput.trim()],
            }));
            setAmenityInput('');
        }
    };

    const handleRemoveAmenity = (index) => {
        setFormData((prevData) => ({
            ...prevData,
            amenities: prevData.amenities.filter((_, i) => i !== index),
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (payloadOverride, e) => {
        if (e && e.preventDefault) e.preventDefault();

        const dataToSend = payloadOverride || formData;
        console.log("Sending to backend:", dataToSend);

        try {
            const response = await axios.put(
                `${config.API_URL}/v2/property/${propertyId}`,
                dataToSend,
                { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
            );
            if (response.status === 200 || response.status === 201) {
                onPropertyUpdated?.(response.data);
                console.log('Property updated successfully:', response.data);
                toast.success('Property updated successfully!');
                onClose();
            } else {
                toast.error('Failed to update property.');
            }
        } catch (err) {
            console.error('Error updating property:', err);
            toast.error('Error updating property.');
        }
    };

    const uploadButton = (
        <button style={{ border: 0, background: 'none' }} type="button">
            <PlusOutlined />
            <div className="font-poppins" style={{ marginTop: 4 }}>Upload</div>
        </button>
    );

    const steps = [
        'Input property details',
        'Upload property image',
    ];

    const handleNext = async () => {
        try {
            let imageId;

            if (propertyImages.length > 0) {
                const file = propertyImages[0];

                if (file.originFileObj) {
                    // Upload new file
                    const [uploadedImageId] = await imageUpload(
                        [{
                            name: file.name,
                            type: file.originFileObj.type,
                            data: file.originFileObj
                        }],
                        "property",
                        propertyId,
                        activeAccountId,
                        ["Property image"]
                    );

                    imageId = uploadedImageId;
                } else {
                    // Keep existing
                    imageId = file.uid;
                }
            }

            const payload = {
                ...formData,
                images: imageId ? [imageId] : [],
            };

            console.log("Payload to backend:", payload);

            if (activeStep === steps.length - 1) {
                await handleSubmit(payload);
            } else {
                setFormData(payload);
                setActiveStep(prev => prev + 1);
            }
        } catch (error) {
            message.error("Failed to upload image. Please try again.");
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    return (
        <div className="popup-overlay">
            <div className="popup-content">
                <button className="close-popup" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>
                <h2 className="text-2xl text-black">Edit Property</h2>
                <div>
                    <Stepper activeStep={activeStep} style={{ marginBottom: '30px' }} alternativeLabel>
                        {steps.map((label, index) => {
                            const stepProps = {};
                            const labelProps = {};
                            return (
                                <Step
                                    key={label}
                                    {...stepProps}
                                    sx={{
                                        "& .MuiStepIcon-root.Mui-active": {
                                            color: "#C86B2C",
                                        },
                                        "& .MuiStepIcon-root.Mui-completed": {
                                            color: "#C86B2C",
                                        },
                                    }}>
                                    <StepLabel {...labelProps}>{label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                    <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                        {activeStep === 0 && (
                            <div>
                                <TextField label="Property Name" name="name" value={formData.name} onChange={handleChange} fullWidth sx={{ marginBottom: '1rem' }} />
                                <TextField label="Developer" name="street" value={formData.developer} onChange={handleChange} fullWidth sx={{ marginBottom: '1rem' }} />
                                <div className="amenities-section">
                                    <div className="amenities-input">
                                        <TextField
                                            label="Add Amenity"
                                            value={amenityInput}
                                            fullWidth
                                            onChange={(e) => setAmenityInput(e.target.value)}
                                        />
                                        <button id="amenity-button" className="button" type="button" onClick={handleAddAmenity}>
                                            <i className="fas fa-plus"></i>
                                        </button>
                                    </div>
                                    <ul style={{ marginBottom: '1rem', fontSize: '14px' }}>
                                        {formData.amenities.map((amenity, index) => (
                                            <li key={index}>
                                                {amenity}{' '}
                                                <button id="delete-amenity-button" type="button" onClick={() => handleRemoveAmenity(index)}>
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}
                        {activeStep === 1 && (
                            <div
                                className="property-img-upload"
                                style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                            >
                                <Upload
                                    name="avatar"
                                    listType="picture-card"
                                    className="property-uploader"
                                    showUploadList={false} // we’ll handle preview & delete ourselves
                                    beforeUpload={(file) => {
                                        const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
                                        if (!isJpgOrPng) {
                                            message.error("You can only upload JPG/PNG file!");
                                            return Upload.LIST_IGNORE;
                                        }
                                        const isLt2M = file.size / 1024 / 1024 < 2;
                                        if (!isLt2M) {
                                            message.error("Image must be smaller than 2MB!");
                                            return Upload.LIST_IGNORE;
                                        }

                                        // Wrap into the same structure as existing images
                                        setPropertyImages([{
                                            uid: file.uid,
                                            name: file.name,
                                            status: "done",
                                            originFileObj: file,
                                        }]);

                                        return false; // prevent auto-upload
                                    }}
                                >
                                    {propertyImages.length > 0 ? (
                                        <div style={{ position: "relative" }}>
                                            <img
                                                src={propertyImages[0].originFileObj
                                                    ? URL.createObjectURL(propertyImages[0].originFileObj)
                                                    : propertyImages[0].url} // this is now the `key`
                                                alt="property"
                                                style={{ width: "100%" }}
                                            />
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPropertyImages([]);
                                                }}
                                                style={{
                                                    position: "absolute",
                                                    top: 6,
                                                    right: 6,
                                                    background: "rgba(0,0,0,0.5)",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "50%",
                                                    cursor: "pointer",
                                                    width: 22,
                                                    height: 22,
                                                }}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                    ) : (
                                        uploadButton
                                    )}
                                </Upload>
                                <p className="font-poppins text-sm text-gray-500 mt-4 px-4" style={{ marginTop: "20px" }}>
                                    Required: Upload image of the property. JPG or PNG file (max 2MB).
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <React.Fragment>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 4, paddingLeft: '10px', paddingRight: '10px' }}>
                        <button
                            className={`stepper-button ${activeStep === 0 ? "disabled" : ""}`}
                            disabled={activeStep === 0}
                            onClick={handleBack}
                        >
                            Back
                        </button>
                        <Box sx={{ flex: '1 1 auto' }} />
                        <Tooltip
                            title={activeStep === 1 && propertyImages.length === 0 ? "Required to upload a property image" : ""}
                        >
                            <span>
                                <button
                                    className="stepper-button"
                                    onClick={handleNext}
                                    disabled={activeStep === 1 && propertyImages.length === 0} // block Finish unless they upload
                                >
                                    {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                </button>
                            </span>
                        </Tooltip>
                    </Box>
                </React.Fragment>
            </div>
        </div>
    );
};

export default EditPropertyForm;